import { create } from 'zustand';
import type { TutorMode, TutorSession, TutorMessage, TutorQueryLimits } from '../types/tutor';
import type { AdaptationRecord } from '../types/adaptive';
import {
  sendTutorQuery,
  submitTutorFeedback,
  submitTutorCheckpointAnswer,
  loadTutorSession,
  createTutorSession,
  getTutorQueryLimits,
  startAdaptationDiscussion,
} from '../services/tutor';
import { auth } from '../services/firebase';
import { useAuthStore } from './auth-store';

const PANEL_WIDTH_KEY = 'cogniprint:tutor-panel-width';
const DEFAULT_PANEL_WIDTH = 400;
export const TUTOR_PANEL_MIN_WIDTH = 320;
export const TUTOR_PANEL_MAX_WIDTH = 900;

function loadStoredPanelWidth(): number {
  if (typeof localStorage === 'undefined') return DEFAULT_PANEL_WIDTH;
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    if (!raw) return DEFAULT_PANEL_WIDTH;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n)) return DEFAULT_PANEL_WIDTH;
    return Math.max(TUTOR_PANEL_MIN_WIDTH, Math.min(TUTOR_PANEL_MAX_WIDTH, n));
  } catch {
    return DEFAULT_PANEL_WIDTH;
  }
}

function persistPanelWidth(width: number): void {
  if (typeof localStorage === 'undefined') return;
  try { localStorage.setItem(PANEL_WIDTH_KEY, String(width)); } catch { /* quota/private mode */ }
}

interface TutorStore {
  // Panel state
  isOpen: boolean;
  panelWidth: number;
  activeMode: TutorMode | null;

  // Session state
  currentSession: TutorSession | null;
  isLoading: boolean;

  // Context
  activeNodeId: string | null;
  highlightedText: string | null;

  // Limits
  queryLimits: TutorQueryLimits | null;

  // Content/path context
  contentId: string | null;
  pathId: string | null;

  // Adaptation discussion mode
  adaptationMode: boolean;
  adaptationRecordContext: AdaptationRecord | null;

  // Actions
  openPanel: (context?: { nodeId?: string; highlightedText?: string }) => void;
  closePanel: () => void;
  setPanelWidth: (width: number, opts?: { persist?: boolean }) => void;
  setMode: (mode: TutorMode | null) => void;
  sendQuery: (query: string) => Promise<void>;
  submitFeedback: (messageId: string, feedback: 'up' | 'down') => Promise<void>;
  submitCheckpointAnswer: (messageId: string, answer: string) => Promise<{ score: number; feedback: string }>;
  loadSession: (sessionId: string) => Promise<void>;
  createNewSession: (contentId: string, pathId: string) => Promise<void>;
  loadQueryLimits: () => Promise<void>;
  setHighlightedText: (text: string | null) => void;
  setContext: (contentId: string, pathId: string, nodeId: string | null) => void;
  enterAdaptationMode: (record?: AdaptationRecord | null) => Promise<void>;
  exitAdaptationMode: () => void;
  reset: () => void;
}

export const useTutorStore = create<TutorStore>((set, get) => ({
  isOpen: false,
  panelWidth: loadStoredPanelWidth(),
  activeMode: null,
  currentSession: null,
  isLoading: false,
  activeNodeId: null,
  highlightedText: null,
  queryLimits: null,
  contentId: null,
  pathId: null,
  adaptationMode: false,
  adaptationRecordContext: null,

  openPanel: (context) => {
    const { contentId, pathId, currentSession } = get();
    set({
      isOpen: true,
      activeNodeId: context?.nodeId ?? get().activeNodeId,
      highlightedText: context?.highlightedText ?? null,
    });
    // Create session on first open if we have context but no session
    if (!currentSession && contentId && pathId) {
      get().createNewSession(contentId, pathId);
    }
  },

  closePanel: () => set({ isOpen: false }),

  setPanelWidth: (width, opts) => {
    const clamped = Math.max(
      TUTOR_PANEL_MIN_WIDTH,
      Math.min(TUTOR_PANEL_MAX_WIDTH, Math.round(width)),
    );
    set({ panelWidth: clamped });
    // Persist only on drag-end to avoid hammering localStorage during drag.
    if (opts?.persist) persistPanelWidth(clamped);
  },

  setMode: (mode) => set({ activeMode: mode }),

  sendQuery: async (query) => {
    const { currentSession, activeMode, activeNodeId, highlightedText, contentId, pathId } = get();
    const userId = auth.currentUser?.uid;
    if (!userId || !currentSession || !contentId || !pathId) return;

    set({ isLoading: true });

    try {
      const tier = useAuthStore.getState().user?.subscription;
      const result = await sendTutorQuery({
        userId,
        contentId,
        pathId,
        sessionId: currentSession.sessionId,
        query,
        mode: activeMode ?? 'freeform',
        contextNodeId: activeNodeId,
        highlightedText,
        tierAllowsFreezes: tier === 'pro' || tier === 'teams',
      });

      // Check for rate limit error
      if ('error' in result) {
        set({
          isLoading: false,
          queryLimits: {
            tier: get().queryLimits?.tier ?? 'free',
            queriesUsed: result.queriesUsed,
            queriesIncluded: result.queriesIncluded,
            canQuery: false,
            limitType: result.limitType as 'lifetime' | 'monthly' | 'unlimited',
          },
        });
        return;
      }

      // Add user message + assistant response to local session
      const userMsg: TutorMessage = {
        messageId: `local_${Date.now()}`,
        role: 'user',
        content: query,
        timestamp: new Date().toISOString(),
        mode: activeMode,
        contextNodeId: activeNodeId,
        conceptId: null,
        highlightedText,
        feedback: null,
        tokenCount: null,
        responseTimeMs: null,
        modelUsed: null,
        visualSpec: null,
        videoResults: null,
        checkpointData: null,
      };

      set((state) => ({
        isLoading: false,
        highlightedText: null,
        activeMode: null,
        currentSession: state.currentSession
          ? {
              ...state.currentSession,
              messages: [...state.currentSession.messages, userMsg, result.message],
              queriesInSession: state.currentSession.queriesInSession + 1,
            }
          : null,
        queryLimits: state.queryLimits
          ? { ...state.queryLimits, queriesUsed: state.queryLimits.queriesUsed + 1, canQuery: result.queriesRemaining > 0 }
          : null,
      }));
    } catch (err) {
      console.error('Tutor query failed:', err);
      set({ isLoading: false });
    }
  },

  submitFeedback: async (messageId, feedback) => {
    const { currentSession } = get();
    if (!currentSession) return;

    await submitTutorFeedback(currentSession.sessionId, messageId, feedback);

    // Update local state
    set((state) => ({
      currentSession: state.currentSession
        ? {
            ...state.currentSession,
            messages: state.currentSession.messages.map((m) =>
              m.messageId === messageId ? { ...m, feedback } : m,
            ),
          }
        : null,
    }));
  },

  submitCheckpointAnswer: async (messageId, answer) => {
    const { currentSession } = get();
    if (!currentSession) throw new Error('No active session');

    const result = await submitTutorCheckpointAnswer(currentSession.sessionId, messageId, answer);

    // Update local state
    set((state) => ({
      currentSession: state.currentSession
        ? {
            ...state.currentSession,
            messages: state.currentSession.messages.map((m) =>
              m.messageId === messageId && m.checkpointData
                ? {
                    ...m,
                    checkpointData: {
                      ...m.checkpointData,
                      userAnswer: answer,
                      score: result.score,
                      feedback: result.feedback,
                      signalClassification: result.signalClassification,
                    },
                  }
                : m,
            ),
          }
        : null,
    }));

    return result;
  },

  loadSession: async (sessionId) => {
    const session = await loadTutorSession(sessionId);
    set({ currentSession: session });
  },

  createNewSession: async (contentId, pathId) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const session = await createTutorSession({ userId, contentId, pathId });
    set({ currentSession: session, contentId, pathId });
  },

  loadQueryLimits: async () => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const limits = await getTutorQueryLimits(userId);
    set({ queryLimits: limits });
  },

  setHighlightedText: (text) => set({ highlightedText: text }),

  setContext: (contentId, pathId, nodeId) => set({ contentId, pathId, activeNodeId: nodeId }),

  enterAdaptationMode: async (record) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;

    set({
      adaptationMode: true,
      adaptationRecordContext: record ?? null,
      activeMode: 'adaptation_discussion',
      isOpen: true,
      isLoading: true,
    });

    try {
      const session = await startAdaptationDiscussion({
        userId,
        contentId: null,
        pathId: null,
        adaptationRecordId: record?.recordId ?? null,
      });
      set({
        currentSession: session,
        contentId: session.contentId,
        pathId: session.pathId,
        isLoading: false,
      });
    } catch (err) {
      console.error('Failed to start adaptation discussion:', err);
      set({ isLoading: false });
    }
  },

  exitAdaptationMode: () => set({
    adaptationMode: false,
    adaptationRecordContext: null,
    activeMode: null,
  }),

  reset: () => set({
    isOpen: false,
    activeMode: null,
    currentSession: null,
    isLoading: false,
    activeNodeId: null,
    highlightedText: null,
    queryLimits: null,
    contentId: null,
    pathId: null,
    adaptationMode: false,
    adaptationRecordContext: null,
  }),
}));
